import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import "./adminTour.css";

const TOUR_STORAGE_KEY = "wgt_admin_tour_v4";
const SCROLL_SETTLE_MS = 700;

const PAGE_STEPS = {
  "/admin/rules": [
    {
      selector: '[data-tour="rules-custom-rules"]',
      title: "Custom rules",
      body: "Edit How to play section titles and bullet lists. Each section has its own title and rules typography.",
    },
    {
      selector: '[data-tour="rules-voting"]',
      title: "Category voting",
      body: "Open or close voting on gallery cards. Reactions stay available either way.",
    },
    {
      selector: '[data-tour="rules-votes-per-category"]',
      title: "Votes per category",
      body: "Set how many entries a player may vote for within one category (default 1).",
    },
    {
      selector: '[data-tour="rules-gallery-display"]',
      title: "Display gallery",
      body: "If false, players see a moderation thank-you message instead of the upload grid.",
    },
    {
      selector: '[data-tour="rules-mosaic"]',
      title: "Enable mosaic",
      body: "Turn on the photo mosaic in the gallery, then open Mosaic to set the word, title, and font sizes.",
    },
    {
      selector: '[data-tour="rules-upload-sharing"]',
      title: "Upload sharing",
      body: "Control whether players can share uploads, allow multiple shares, and set the share limit.",
    },
    {
      selector: '[data-tour="rules-colors"]',
      title: "Colors",
      body: "Adjust text and button colors used across the player experience.",
    },
    {
      selector: '[data-tour="rules-upload-rules"]',
      title: "Game upload RULES",
      body: "These bullets appear under RULES on the player game upload step. Typography here is independent.",
    },
    {
      selector: '[data-tour="rules-save"]',
      title: "Save & Continue",
      body: "Save all rules and customization settings, then continue to category customization.",
    },
  ],
  "/admin/jury": [
    {
      selector: '[data-tour="jury-max-members"]',
      title: "Max jury members",
      body: "Limit how many jury members can be added for this session.",
    },
    {
      selector: '[data-tour="jury-max-points"]',
      title: "Max points per entry",
      body: "Highest score a jury member can give a single entry.",
    },
    {
      selector: '[data-tour="jury-percentage"]',
      title: "Jury points percentage",
      body: "Weight of jury scores vs public likes in the final ranking formula.",
    },
    {
      selector: '[data-tour="jury-save-settings"]',
      title: "Save jury settings",
      body: "One button saves max members, max points, and percentage together.",
    },
    {
      selector: '[data-tour="jury-add-member"]',
      title: "Add jury member",
      body: "Enter name, email, employee ID, and phone with country code (e.g. +91).",
    },
    {
      selector: '[data-tour="jury-list"]',
      title: "Jury list",
      body: "Only members for the current session are shown here.",
    },
  ],
  "/admin/customize-spot": [
    {
      selector: '[data-tour="customize-titles"]',
      title: "Manage categories",
      body: "Add, remove, and rename talent categories shown to players.",
    },
    {
      selector: '[data-tour="customize-descriptions"]',
      title: "Category descriptions",
      body: "Enable descriptions and write hover text for each category icon on the game page.",
    },
    {
      selector: '[data-tour="customize-icons"]',
      title: "Category images",
      body: "Upload a square icon for each category (100×100 px).",
    },
    {
      selector: '[data-tour="customize-addl-fields"]',
      title: "Additional text fields",
      body: "Add up to 8 extra input fields on the player submission form (e.g. employee id).",
    },
  ],
  "/admin/uploads": [
    {
      selector: '[data-tour="admin-uploads"]',
      title: "Gallery uploads",
      body: "Review player submissions and hide or show entries in the public gallery.",
    },
  ],
  "/admin/mosaic": [
    {
      selector: '[data-tour="mosaic-title"]',
      title: "Mosaic title",
      body: "This heading appears above the photo mosaic on the player page.",
    },
    {
      selector: '[data-tour="mosaic-typography"]',
      title: "Title typography",
      body: "Set the title font, desktop/mobile size, and color.",
    },
    {
      selector: '[data-tour="mosaic-word"]',
      title: "Mosaic word",
      body: "Letters of this word are filled with gallery photos. A space splits it onto two lines.",
    },
    {
      selector: '[data-tour="mosaic-font-desktop"]',
      title: "Desktop word size",
      body: "Maximum auto-fit size of the mosaic word on desktop (e.g. 16rem).",
    },
    {
      selector: '[data-tour="mosaic-font-mobile"]',
      title: "Mobile word size",
      body: "Maximum auto-fit size below 768px width (e.g. 4rem).",
    },
    {
      selector: '[data-tour="mosaic-save"]',
      title: "Save mosaic settings",
      body: "Save title, word, and font sizes. Mosaic must be enabled on the Rules page first.",
    },
  ],
};

function resolveSteps(pathname) {
  const cleaned =
    pathname.endsWith("/") && pathname !== "/" ? pathname.slice(0, -1) : pathname;
  return PAGE_STEPS[cleaned] || [];
}

function isRulesPage(pathname) {
  const cleaned =
    pathname.endsWith("/") && pathname !== "/" ? pathname.slice(0, -1) : pathname;
  return cleaned === "/admin/rules";
}

export default function AdminTour() {
  const location = useLocation();
  const steps = useMemo(() => resolveSteps(location.pathname), [location.pathname]);
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const scrollingRef = useRef(false);
  const settleTimerRef = useRef(null);
  const rafRef = useRef(null);

  const step = steps[index] || null;

  const readRect = useCallback(() => {
    if (!step?.selector) {
      setRect(null);
      return null;
    }
    const el = document.querySelector(step.selector);
    if (!el) {
      setRect(null);
      return null;
    }
    const r = el.getBoundingClientRect();
    const next = {
      top: r.top,
      left: r.left,
      width: Math.max(r.width, 40),
      height: Math.max(r.height, 40),
    };
    setRect(next);
    return { el, next };
  }, [step]);

  const stopTracking = useCallback(() => {
    scrollingRef.current = false;
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const trackDuringScroll = useCallback(() => {
    const tick = () => {
      if (!scrollingRef.current) return;
      readRect();
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [readRect]);

  const measure = useCallback(() => {
    const result = readRect();
    if (!result?.el) return;
    stopTracking();
    scrollingRef.current = true;
    trackDuringScroll();
    result.el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    settleTimerRef.current = window.setTimeout(() => {
      readRect();
      scrollingRef.current = false;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      // Final settle after layout reflow
      window.setTimeout(() => readRect(), 80);
    }, SCROLL_SETTLE_MS);
  }, [readRect, stopTracking, trackDuringScroll]);

  useEffect(() => {
    if (!open) {
      stopTracking();
      return undefined;
    }
    const t = setTimeout(measure, 60);
    const onResize = () => {
      if (!scrollingRef.current) readRect();
    };
    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", onResize);
      stopTracking();
    };
  }, [open, measure, readRect, index, stopTracking]);

  useEffect(() => {
    setOpen(false);
    setIndex(0);
    if (!isRulesPage(location.pathname)) return undefined;
    try {
      const seen = localStorage.getItem(TOUR_STORAGE_KEY);
      if (!seen && resolveSteps(location.pathname).length) {
        const t = setTimeout(() => setOpen(true), 700);
        return () => clearTimeout(t);
      }
    } catch {
      /* ignore */
    }
    return undefined;
  }, [location.pathname]);

  const close = (persist = true) => {
    setOpen(false);
    stopTracking();
    if (persist) {
      try {
        localStorage.setItem(TOUR_STORAGE_KEY, "1");
      } catch {
        /* ignore */
      }
    }
  };

  const next = () => {
    if (index >= steps.length - 1) {
      close(true);
      return;
    }
    setIndex((i) => i + 1);
  };

  const prev = () => setIndex((i) => Math.max(0, i - 1));

  const startTour = () => {
    if (!steps.length) return;
    setIndex(0);
    setOpen(true);
  };

  let tipStyle = { top: "28%", left: "50%", transform: "translateX(-50%)" };
  if (rect) {
    const cardH = 200;
    const gap = 16;
    const below = rect.top + rect.height + gap;
    const above = rect.top - cardH - gap;
    const top =
      below + cardH < window.innerHeight - 16
        ? below
        : Math.max(16, above);
    tipStyle = {
      top,
      left: Math.min(Math.max(16, rect.left), window.innerWidth - 340),
    };
  }

  return (
    <>
      <button
        type="button"
        className="admin-tour-trigger"
        onClick={startTour}
        disabled={!steps.length}
        title={steps.length ? "Start tour" : "No tour on this page"}
      >
        <i className="fa-solid fa-circle-question" aria-hidden /> Tour
      </button>

      {open && step ? (
        <div className="admin-tour-root" role="dialog" aria-modal="true" aria-label="Admin tour">
          <div className="admin-tour-backdrop" onClick={() => close(true)} />
          {rect ? (
            <div
              className="admin-tour-spotlight"
              style={{
                top: rect.top - 8,
                left: rect.left - 8,
                width: rect.width + 16,
                height: rect.height + 16,
              }}
            />
          ) : null}
          <div className="admin-tour-card" style={tipStyle}>
            <div className="admin-tour-stepcount">
              {index + 1} / {steps.length}
            </div>
            <h3>{step.title}</h3>
            <p>{step.body}</p>
            {!rect ? (
              <p className="admin-tour-missing">
                This section is not visible yet — scroll or expand it, then click Next.
              </p>
            ) : null}
            <div className="admin-tour-actions">
              <button type="button" className="admin-tour-skip" onClick={() => close(true)}>
                Skip
              </button>
              <div className="admin-tour-nav">
                <button type="button" onClick={prev} disabled={index === 0}>
                  Back
                </button>
                <button type="button" className="admin-tour-next" onClick={next}>
                  {index >= steps.length - 1 ? "Done" : "Next"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
